/**
 * @fileoverview A small in-memory buffer of recently shown toast messages.
 *
 * Toast notifications are rendered in a top-level container outside the page
 * regions that the Operator page scraper reads, and auto-closing toasts
 * (info/success/warning) disappear after ~10s. To let the BrazeAI Operator
 * understand what just happened (e.g. "the preview timed out") even after a
 * toast has auto-closed, every toast is recorded here as it is shown and the
 * page scraper folds recent entries into the page state it reports to the LLM.
 *
 * This lives behind the single `makeToast` chokepoint, which reads each toast
 * out of the DOM as it opens, so every toast is captured with no per-feature
 * wiring and a React-node body is no more opaque than a string one. An entry
 * is therefore written the same way the live toasts around it are read, and
 * dedupes against its own toast while both are in play (see `readToasts`).
 *
 * Only a toast that actually reached the screen is here: `makeToast` records
 * from the toast's own `onOpen`, so a call the Toaster drops or queues (see
 * `findToastElement`) records if and when it opens, and not before. The user
 * has not seen it either, so it is not yet page state.
 */

import type { ToastIntent } from "./types";

export type RecentToast = {
  intent: ToastIntent;
  text: string;
  /** Epoch milliseconds when the toast was shown. */
  timestamp: number;
};

/** Maximum number of recent toasts retained. Oldest entries are evicted. */
const maxRecentToasts = 5;

/** Default max age (ms) for `getRecentToasts`. */
export const defaultRecentToastMaxAgeMs = 60_000;

/** Newest entries are kept at the front of the buffer. */
let recentToasts: RecentToast[] = [];

/**
 * Record a toast as it is shown, given the text it shows. `makeToast` reads
 * that text off the rendered toast with `readToastText`, so it arrives already
 * escaped and on one line, and empty means there was nothing to read.
 */
export function recordToast(intent: ToastIntent, content: string): void {
  const text = content.trim();
  if (!text) {
    return;
  }
  // Date.now() is used (rather than getNowTimestamp) because this @lib module
  // must not depend on @services, and we only need a monotonic recency marker.
  recentToasts = [{ intent, text, timestamp: Date.now() }, ...recentToasts].slice(0, maxRecentToasts);
}

/**
 * Return recent toasts newer than `maxAgeMs`, most-recent-first.
 */
export function getRecentToasts(maxAgeMs: number = defaultRecentToastMaxAgeMs): RecentToast[] {
  const cutoff = Date.now() - maxAgeMs;
  return recentToasts.filter((toast) => toast.timestamp >= cutoff);
}

/** Clear the buffer. Intended for tests. */
export function clearRecentToasts(): void {
  recentToasts = [];
}
